import { ActionType } from 'typesafe-actions';

import PageMeta from '@interfaces/page-meta';
import Settings from '@interfaces/settings';
import HeadMenu from '@interfaces/head-menu';
import CustomPage from '@interfaces/custom-page';
import cutTags from 'client/helpers/cutTags';

import * as types from './types';
import * as actions from './actions';

export type ApplicationAction = ActionType<typeof actions>;

export interface ApplicationState {
    readonly settings: Settings;
    readonly appMenu: HeadMenu;
    readonly customPage: Partial<CustomPage>;
    readonly isLoading: boolean;
    readonly error?: boolean;
    readonly pageMeta: PageMeta;
    readonly sideNavIsOpened?: boolean;
}

const initialState: ApplicationState = {
    settings: {},
    appMenu: { items: [] },
    customPage: {},
    pageMeta: { title: 'Research' },
    sideNavIsOpened: false,
    isLoading: false,
    error: undefined
};

/* tslint:disable-next-line cyclomatic-complexity */
function reducer(state: ApplicationState = initialState, action: ApplicationAction): ApplicationState {
    switch (action.type) {
        case types.INIT_APPLICATION:
            return initApplication(state);

        case types.FETCH_SETTING_SUCCESS:
            return fetchSettingsSuccess(state, action);

        case types.FETCH_SETTING_ERROR:
            return fetchSettingsError(state, action);

        case types.FETCH_APP_MENU_SUCCESS:
            return fetchAppMenuSuccess(state, action);

        case types.FETCH_APP_MENU_ERROR:
            return fetchAppMenuError(state, action);

        case types.FETCH_CUSTOM_PAGE:
            return fetchCustomPage(state);

        case types.FETCH_CUSTOM_PAGE_SUCCESS:
            return fetchCustomPageSuccess(state, action);

        case types.FETCH_CUSTOM_PAGE_ERROR:
            return fetchCustomPageError(state, action);

        case types.SET_PAGE_META:
            return setPageMeta(state, action);

        case types.TOGGLE_SIDENAV:
            return toggleSideNav(state, action);

        default:
            return state;
    }
}

function initApplication(state: ApplicationState) {
    return {
        ...state,
        isLoading: true,
        error: false
    };
}

function fetchSettingsSuccess(state: ApplicationState, action: any) {
    return {
        ...state,
        settings: {
            ...action.payload,
            title: cutTags(action.payload.title),
            seoDescription: cutTags(action.payload.seoDescription),
            metaKeywords: cutTags(action.payload.metaKeywords)
        },
        isLoading: false,
        error: false
    };
}

function fetchSettingsError(state: ApplicationState, action: any) {
    return {
        ...state,
        settings: {},
        isLoading: false,
        error: action.payload
    };
}

function fetchAppMenuSuccess(state: ApplicationState, action: any) {
    return {
        ...state,
        appMenu: action.payload,
        isLoading: false,
        error: false
    };
}

function fetchAppMenuError(state: ApplicationState, action: any) {
    return {
        ...state,
        appMenu: { items: [] },
        isLoading: false,
        error: action.payload
    };
}

function fetchCustomPage(state: ApplicationState) {
    return {
        ...state,
        customPage: {},
        isLoading: true,
        error: false
    };
}

function fetchCustomPageSuccess(state: ApplicationState, action: any) {
    return {
        ...state,
        customPage: action.payload,
        isLoading: false,
        error: false
    };
}

function fetchCustomPageError(state: ApplicationState, action: any) {
    return {
        ...state,
        customPage: {},
        isLoading: false,
        error: action.payload
    };
}

function setPageMeta(state: ApplicationState, action: any) {
    return {
        ...state,
        pageMeta: action.payload
    };
}

function toggleSideNav(state: ApplicationState, { payload: isOpened }: any) {
    return {
        ...state,
        sideNavIsOpened: isOpened
    };
}

export default reducer;
